/** The marketplace: resources traded for resources.
 *
 * The exchange rate is a rule output and this screen does not own it. It is
 * handed a `rate` probe that the host got from the kernel's own `tradeYield`,
 * so the number on screen is the number the command will produce - including
 * the part a player is most likely to disbelieve, that the spread narrows as
 * the kingdom builds more Marketplaces. Recomputing the ratio here from
 * `resourceValue` would be a second implementation of a rule (I10), and it
 * would go stale the first time the efficiency table moves.
 *
 * The rate is asked for again on every amount, not scaled from a unit price:
 * the kernel floors once at the end, so 3 ore is not always three times what 1
 * ore fetches, and a UI that multiplies would promise a number the kernel then
 * refuses to pay.
 */

import { useCallback, useMemo, useState } from "react";
import type {
  ContentRegistry, GameCommand, PlayerId, Reason, ResourceKind, Resources,
} from "@game/core";
import { RESOURCES } from "@game/core";
import { RESOURCE_ICON } from "../theme/art.js";
import { groupDigits, titleCase } from "./format.js";
import { ICON_SMALL, ReasonLine } from "./parts.js";
import { MaybeIcon, ScreenDialog, ScreenStyles } from "./primitives.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

/** How far the "what is the smallest useful trade" search will walk. The same
 *  bound the kernel's own enumerator uses; it is a search limit, not a rule. */
const UNIT_SEARCH_CAP = 1000;

/** What the kernel says a given amount fetches. Every field comes from `rate`. */
export interface TradeQuote {
  readonly give: ResourceKind;
  readonly take: ResourceKind;
  readonly amount: number;
  /** What the kernel would pay for `amount`. */
  readonly gain: number;
  /** The smallest amount that buys anything at all, and what it buys. */
  readonly unit: { readonly give: number; readonly gain: number } | null;
}

export type TradeRate = (
  give: ResourceKind, take: ResourceKind, amount: number,
) => number;

/**
 * The quote for one pair, entirely in the kernel's numbers.
 *
 * `unit` is found by asking the kernel for 1, 2, 3 ... rather than by dividing
 * two resource values: with integer floors the first amount that pays anything
 * is the only honest way to state a rate, and it is the same walk
 * `enumerateLegalCommands` does to decide the trade is worth offering.
 */
export function quoteTrade(
  rate: TradeRate, have: number, give: ResourceKind, take: ResourceKind, amount: number,
): TradeQuote {
  if (give === take) return { give, take, amount, gain: 0, unit: null };
  let unit: TradeQuote["unit"] = null;
  const cap = have < UNIT_SEARCH_CAP ? have : UNIT_SEARCH_CAP;
  for (let n = 1; n <= cap; n++) {
    const got = rate(give, take, n);
    if (got > 0) { unit = { give: n, gain: got }; break; }
  }
  return { give, take, amount, gain: amount <= 0 ? 0 : rate(give, take, amount), unit };
}

/** The pair a freshly opened marketplace should start on: the first resource
 *  the player actually has, traded for the first one they do not. */
export function openingPair(
  treasury: Resources,
): { readonly give: ResourceKind; readonly take: ResourceKind } {
  const give = RESOURCES.find((r) => r !== "gold" && treasury[r] > 0)
    ?? RESOURCES.find((r) => treasury[r] > 0) ?? "wood";
  const take = RESOURCES.find((r) => r !== give) ?? "gold";
  return { give, take };
}

export interface MarketplaceProps {
  readonly content: ContentRegistry;
  readonly player: PlayerId;
  readonly treasury: Resources;
  /** The kernel's `tradeYield`, handed over by the host. */
  readonly rate: TradeRate;
  /** From the kernel: null means "this trade would go through". */
  readonly tradeReason: (give: ResourceKind, take: ResourceKind, amount: number) => Reason | null;
  readonly onCommand: (cmd: GameCommand) => void;
  readonly onClose: () => void;
}

/**
 * How much to put on the counter.
 *
 * `parts.tsx` already has a `CountSlider` and this screen deliberately does not
 * use it: `.hs-armyrow__head`, the class its label row is built on, is only
 * styled under `.hs-hero`, so inside a dialog the row collapses and the label
 * runs into its own value ("Wood to give15 / 15"). Fixing that class is a
 * change to a shared control the recruit dialog also uses; this row is local
 * and built out of classes the screens' stylesheet actually defines.
 */
function AmountRow(props: {
  readonly label: string;
  readonly value: number;
  readonly max: number;
  readonly onChange: (n: number) => void;
}): JSX.Element {
  const clamp = useCallback((n: number): number => {
    if (!Number.isFinite(n)) return 0;
    const v = Math.trunc(n);
    return v < 0 ? 0 : v > props.max ? props.max : v;
  }, [props.max]);

  return (
    <div className="hs-field">
      <div className="hs-row">
        <span className="hs-field__label">{props.label}</span>
        <span className="hs-spacer" />
        <span className="hs-num">
          {`${groupDigits(props.value)} of ${groupDigits(props.max)}`}
        </span>
      </div>
      <input
        className="hs-range"
        type="range"
        min={0}
        max={props.max}
        step={1}
        value={props.value}
        aria-label={props.label}
        onChange={(e) => props.onChange(clamp(Number(e.target.value)))}
      />
      <div className="hs-choices">
        <button type="button" className="hs-btn hs-btn--ghost"
          onClick={() => props.onChange(clamp(props.value - 1))}>-1</button>
        <input
          className="hs-input"
          style={{ width: "96px" }}
          inputMode="numeric"
          value={String(props.value)}
          aria-label={`${props.label} amount`}
          onChange={(e) => props.onChange(clamp(Number(e.target.value)))}
        />
        <button type="button" className="hs-btn hs-btn--ghost"
          onClick={() => props.onChange(clamp(props.value + 1))}>+1</button>
        <button type="button" className="hs-btn hs-btn--ghost"
          onClick={() => props.onChange(props.max)}>Max</button>
      </div>
    </div>
  );
}

function ResourcePicker(props: {
  readonly label: string;
  readonly value: ResourceKind;
  readonly treasury: Resources;
  readonly disabled: ResourceKind | null;
  readonly showHeld: boolean;
  readonly onPick: (r: ResourceKind) => void;
}): JSX.Element {
  return (
    <div className="hs-field">
      <span className="hs-field__label">{props.label}</span>
      <div className="hs-choices">
        {RESOURCES.map((r) => (
          <button
            type="button"
            key={r}
            className={`hs-choice${r === props.value ? " hs-choice--on" : ""}`}
            disabled={r === props.disabled}
            aria-pressed={r === props.value}
            aria-label={`${props.label} ${titleCase(r)}`}
            onClick={() => props.onPick(r)}
          >
            <MaybeIcon atlasKey={RESOURCE_ICON[r]} size={ICON_SMALL} title={r} />
            {/* Both rows name the resource. The give row used to print only the
                held count, so seven unlabelled piles of numbers faced seven
                labelled piles and the two rows did not read as the same list. */}
            <span className="hs-choice__sub">{titleCase(r)}</span>
            {props.showHeld ? (
              <span className="hm-mkt__held">{groupDigits(props.treasury[r])}</span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Marketplace(props: MarketplaceProps): JSX.Element {
  useMarketStyles();
  const { rate, treasury } = props;
  const opening = useMemo(() => openingPair(treasury), [treasury]);
  const [give, setGive] = useState<ResourceKind>(opening.give);
  const [take, setTake] = useState<ResourceKind>(opening.take);
  const [amount, setAmount] = useState(0);

  const have = treasury[give];
  const clamped = amount > have ? have : amount;

  const quote = useMemo(
    () => quoteTrade(rate, have, give, take, clamped),
    [rate, have, give, take, clamped],
  );
  // At zero there is no trade to refuse; the kernel would say TARGET_INVALID
  // and printing that on a freshly opened screen reads as a broken marketplace.
  const reason = clamped > 0 ? props.tradeReason(give, take, clamped) : null;

  const pickGive = useCallback((r: ResourceKind) => {
    setGive(r);
    setAmount(0);
    if (r === take) setTake(RESOURCES.find((x) => x !== r) ?? "gold");
  }, [take]);

  const pickTake = useCallback((r: ResourceKind) => {
    setTake(r);
    if (r === give) setGive(RESOURCES.find((x) => x !== r) ?? "wood");
  }, [give]);

  const trade = useCallback(() => {
    props.onCommand({ type: "TRADE_RESOURCE", player: props.player, give, take, amount: clamped });
    setAmount(0);
  }, [props, give, take, clamped]);

  const legal = reason === null && clamped > 0 && quote.gain > 0;

  return (
    <ScreenDialog title="Marketplace" onClose={props.onClose} width={720}>
      {/* Mounted by the town screen today, but this dialog must also stand on
          its own the moment anything else opens it. */}
      <ScreenStyles />
      <div className="hs-tiny hs-muted" style={{ marginBottom: "10px" }}>
        The traders take their cut off the top. Every further Marketplace your
        kingdom builds narrows the spread, and this rate is quoted at the one
        you have now.
      </div>

      <ResourcePicker
        label="You give"
        value={give}
        treasury={treasury}
        disabled={null}
        showHeld
        onPick={pickGive}
      />

      <div style={{ height: "8px" }} />

      <ResourcePicker
        label="You receive"
        value={take}
        treasury={treasury}
        disabled={give}
        showHeld={false}
        onPick={pickTake}
      />

      <div style={{ height: "10px" }} />

      <AmountRow
        value={clamped}
        max={have}
        onChange={setAmount}
        label={`${titleCase(give)} to give`}
      />

      {/* The counter itself. It was a cold blue-grey slab, the one thing on a
          wood-and-brass dialog that belonged to a different game. */}
      <div className="hs-card hm-mkt__deal" style={{ marginTop: "10px" }}>
        <div className="hs-card__title">The deal</div>
        <div className="hs-row" style={{ gap: "12px", fontSize: "max(18px, var(--hm-type-floor))" }}>
          <span className="hs-cost__item hs-cost__item--ok">
            <MaybeIcon atlasKey={RESOURCE_ICON[give]} size={ICON_SMALL} title={give} />
            <span className="hs-num" data-qa="market-give">{groupDigits(clamped)}</span>
          </span>
          <span aria-hidden="true">&rarr;</span>
          <span className="hs-cost__item hs-cost__item--ok">
            <MaybeIcon atlasKey={RESOURCE_ICON[take]} size={ICON_SMALL} title={take} />
            <span className="hs-num" data-qa="market-gain">{groupDigits(quote.gain)}</span>
          </span>
        </div>
        <div className="hs-tiny hs-muted" style={{ marginTop: "6px" }} data-qa="market-rate">
          {quote.unit === null
            ? `You do not hold enough ${titleCase(give)} to buy a single ${titleCase(take)}.`
            : `Today's rate: ${groupDigits(quote.unit.give)} ${titleCase(give)}`
              + ` for ${groupDigits(quote.unit.gain)} ${titleCase(take)}.`}
        </div>
      </div>

      <ReasonLine content={props.content} reason={reason} lead="Cannot trade:" />

      <div style={{ marginTop: "10px" }}>
        <button
          type="button"
          className="hs-btn hm-mkt__trade"
          style={{ width: "100%" }}
          disabled={!legal}
          title={reason === null ? undefined : "The kernel refuses this trade"}
          onClick={trade}
        >
          Trade
        </button>
      </div>
    </ScreenDialog>
  );
}

// ------------------------------------------------------------------ styles

const MARKET_STYLE_ID = "hm-marketplace";

const MARKET_CSS = `
/* The counter the deal is struck on: a parchment docket pinned to the wood,
   not the slate-blue slab it replaces. */
.hs-card.hm-mkt__deal {
  color: #2c2008;
  background-color: #dcc697;
  background-image:
    repeating-linear-gradient(91deg, rgba(120,92,48,0.05) 0 1px, rgba(0,0,0,0) 1px 4px),
    radial-gradient(120% 140% at 50% 8%, #f2e3bd 0%, #dcc697 52%, #b79f72 100%);
  box-shadow:
    inset 0 0 0 1px rgba(70,52,26,0.75),
    inset 0 0 26px rgba(120,88,42,0.25),
    0 2px 6px rgba(0,0,0,0.45);
}
.hs-card.hm-mkt__deal .hs-card__title { color: #6d5017; }
.hs-card.hm-mkt__deal .hs-num { color: #2c2008; text-shadow: 0 1px 0 rgba(255,248,222,0.6); }
.hs-card.hm-mkt__deal .hs-tiny,
.hs-card.hm-mkt__deal .hs-muted { color: #4a3a1c; }
/* The held count under each pile in the give row. */
.hm-mkt__held {
  display: block;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(12px, var(--hm-type-floor));
  letter-spacing: 0.04em;
  color: #f0d9a0;
}
.hs-choice--on .hm-mkt__held { color: #3a2a06; }
/* Struck bronze, the same metal as the spellbook's Cast plate. The shared gold
   button paints a dark-gold label on a gold face, which on the shipped shot left
   TRADE reading as an empty bar. */
.hs-btn.hm-mkt__trade {
  padding: 10px 24px 11px;
  border: 0;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(15px, var(--hm-type-floor));
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #fff2cd;
  background-color: #6a4d1c;
  background-image:
    radial-gradient(80% 62% at 30% 10%, rgba(255,246,220,0.48) 0%, rgba(255,246,220,0) 60%),
    linear-gradient(172deg, #b8913f 0%, #7d5c22 52%, #402e0e 100%);
  box-shadow:
    inset 0 0 0 1px rgba(38,26,6,0.9),
    inset 0 2px 0 rgba(255,246,220,0.6),
    inset 0 -3px 6px rgba(0,0,0,0.5),
    0 1px 0 rgba(255,252,235,0.75),
    0 4px 9px rgba(0,0,0,0.5);
  text-shadow: 0 -1px 0 rgba(0,0,0,0.6), 0 1px 0 rgba(255,236,186,0.28);
  clip-path: polygon(10px 0%, 100% 0%, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0% 100%, 0% 10px);
}
.hs-btn.hm-mkt__trade:hover:not(:disabled) { filter: brightness(1.14) saturate(1.05); }
.hs-btn.hm-mkt__trade:disabled {
  cursor: not-allowed;
  color: #8a7854;
  background-image: linear-gradient(172deg, #6d6048 0%, #4f4433 52%, #2e2619 100%);
  box-shadow:
    inset 0 0 0 1px rgba(38,30,10,0.75),
    inset 0 2px 0 rgba(255,244,212,0.18),
    inset 0 -3px 6px rgba(0,0,0,0.35);
  text-shadow: 0 1px 0 rgba(0,0,0,0.35);
}
`;

function useMarketStyles(): void {
  useStyleSheet(MARKET_STYLE_ID, MARKET_CSS, StyleLayer.screen);
}
